import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, RefreshCw, Webhook, XCircle } from "lucide-react";
import { toast } from "sonner";
import { http } from "../../lib/api";
import { apiError } from "../../context/AuthContext";
import { useAdminList, AdminHead, PrimaryBtn } from "./adminUi";

const Action = ({ label, icon: Icon, onClick, testid }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onClick();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <PrimaryBtn onClick={run} disabled={busy} className="flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Working..." : label}
    </PrimaryBtn>
  );
};

export default function AdminPrintify() {
  const { data: s, reload } = useAdminList("/admin/printify/status");
  if (!s) return <div className="h-64 rounded-3xl bg-white animate-pulse" />;
  const sync = async () => {
    const r = (await http.post("/admin/printify/sync-products")).data;
    toast.success(`Imported ${r.created} new, updated ${r.updated} products`);
    reload();
  };
  const hooks = async () => {
    await http.post("/admin/printify/webhooks", { origin_url: window.location.origin });
    toast.success("Webhooks registered for this site");
    reload();
  };
  const refresh = async () => {
    const r = (await http.post("/admin/printify/refresh-open-orders")).data;
    toast.success(`Refreshed ${r.refreshed} open orders`);
    reload();
  };
  const hookOk = s.webhooks?.length && s.webhooks.every((w) => w.url.startsWith(window.location.origin));
  return (
    <div data-testid="admin-printify">
      <AdminHead title="Printify" sub="Products, fulfillment and tracking sync with your Printify shop. Your API token stays on the server." />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-3xl bg-white shadow-sm p-6" data-testid="printify-connection">
          <p className="text-xs font-bold uppercase tracking-wider text-navy/50">Connection</p>
          <p className="mt-2 flex items-center gap-2 text-xl font-extrabold text-navy">
            {s.connected ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <XCircle className="w-6 h-6 text-tpink" />}
            {s.connected ? s.shop_title || "Connected" : "MOCK mode — not connected"}
          </p>
          <p className="text-sm text-navy/60 mt-1">Shop ID {s.shop_id || "—"} · {s.mode}</p>
          <p className="text-sm text-navy/60 mt-1">New orders: {s.auto_production ? "sent to production automatically" : "created in Printify on hold — approve them in Printify"}</p>
          {s.error && <p className="mt-2 text-sm text-red-600">{s.error}</p>}
        </div>
        <div className="rounded-3xl bg-white shadow-sm p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-navy/50">Products</p>
          <p className="mt-2 text-sm text-navy/70">Imports new Printify products as <b>drafts</b> and refreshes variants, prices, costs and images of existing ones.</p>
          {s.last_sync && <p className="mt-2 text-xs text-navy/50" data-testid="printify-last-sync">Last sync {new Date(s.last_sync.at).toLocaleString()} · {s.last_sync.created} new · {s.last_sync.updated} updated · {s.last_sync.archived_missing} archived</p>}
          <div className="mt-4 flex gap-2 flex-wrap"><Action label="Sync products" icon={RefreshCw} onClick={sync} testid="printify-sync-button" /><Link to="/admin/products" className="h-11 px-4 rounded-full border-2 border-navy font-semibold text-navy grid place-items-center text-sm">Review products</Link></div>
        </div>
        <div className="rounded-3xl bg-white shadow-sm p-6">
          <p className="text-xs font-bold uppercase tracking-wider text-navy/50">Orders & tracking</p>
          <p className="mt-2 text-sm text-navy/70">{s.open_orders} open · <span className={s.failed_orders ? "text-red-600 font-bold" : ""}>{s.failed_orders} need attention</span></p>
          <p className="mt-1 text-xs text-navy/50">Status and tracking arrive automatically via webhooks; refresh to reconcile manually.</p>
          <div className="mt-4"><Action label="Refresh open orders" icon={RefreshCw} onClick={refresh} testid="printify-refresh-orders" /></div>
        </div>
      </div>
      <div className="mt-6 rounded-3xl bg-white shadow-sm p-6" data-testid="printify-webhooks">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-bold text-navy flex items-center gap-2"><Webhook className="w-4 h-4" /> Webhooks {hookOk ? <span className="text-xs rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5">active for this site</span> : <span className="text-xs rounded-full bg-sun/40 text-navy px-2 py-0.5">register for this site</span>}</p>
            <p className="text-xs text-navy/50 mt-1">After publishing to your live domain, open this page there and click Register again.</p>
          </div>
          <Action label="Register webhooks" icon={Webhook} onClick={hooks} testid="printify-register-webhooks" />
        </div>
        <ul className="mt-4 text-sm text-navy/70 space-y-1">{s.webhooks?.map((w) => <li key={w.topic} className="[overflow-wrap:anywhere]"><b>{w.topic}</b> → {w.url}</li>)}</ul>
      </div>
    </div>
  );
}
